import type { EchoMissionDetail, EchoChannel } from '@vtak/shared';
import { apiClient } from '@/services/api-client';
import { useMissionsStore } from '@/stores/missions-store';
import { Radio, Users, Volume2 } from 'lucide-react';

/** Channels section showing active channels with listen buttons */
export function EchoChannels({ detail }: { detail: EchoMissionDetail }): React.JSX.Element {
  const listeningChannelId = useMissionsStore((s) => s.livekit?.channelId ?? null);

  async function handleListen(e: React.MouseEvent, ch: EchoChannel): Promise<void> {
    e.stopPropagation();
    const result = await apiClient.joinChannelLive(ch.id, detail.joinCode);
    if (result) {
      useMissionsStore.getState().setLivekit(result.token, result.wsUrl, ch.id);
    } else {
      useMissionsStore.getState().setAudioError('Failed to join live audio');
    }
  }

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Radio className="h-3.5 w-3.5" />
        Channels
      </h3>
      {detail.channels.length === 0 ? (
        <p className="text-sm text-muted-foreground">No channels</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {detail.channels.map((ch) => (
            <ChannelItem
              key={ch.id}
              channel={ch}
              isListening={listeningChannelId === ch.id}
              onListen={handleListen}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Single channel row with listen toggle and participant list */
function ChannelItem({
  channel,
  isListening,
  onListen,
}: {
  channel: EchoChannel;
  isListening: boolean;
  onListen: (e: React.MouseEvent, ch: EchoChannel) => Promise<void>;
}): React.JSX.Element {
  return (
    <li
      className={`rounded-md px-3 py-2 text-sm transition-colors ${
        isListening
          ? 'border border-green-500/30 bg-green-500/10'
          : 'bg-white/[0.04] hover:bg-white/[0.08]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium">{channel.name}</span>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Users className="h-3 w-3" />
            {channel.memberCount}
          </span>
          <button
            onClick={(e) => onListen(e, channel)}
            className={`rounded p-1 transition-colors ${
              isListening
                ? 'bg-green-500/20 text-green-400'
                : 'text-muted-foreground hover:bg-green-500/10 hover:text-green-400'
            }`}
            title={isListening ? 'Listening' : 'Listen live'}
          >
            <Volume2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {channel.participants.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {channel.participants.map((p) => (
            <span key={p.uid} className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {p.firstname} {p.lastname}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}
